/**
 * Asking for review — the act that enters `in_review` (§7.4, §7.6).
 *
 * There is no other way in. `assertReachableStatus` on the server refuses the
 * string outright, on the grounds that the state without its request row,
 * reviewer set, and owner auto-add is a gate with nothing behind it — so this
 * dialog is the edge itself rather than a form that sets a status.
 *
 * Two things it insists on saying, because both are surprising the first time:
 *
 * - **The owner is already a required reviewer.** §7.3 auto-adds them by
 *   trigger, whatever this form sends, and migration 0004 gives the reason: a
 *   missing row is indistinguishable from a satisfied one, so the required set is
 *   never allowed to start empty. Naming the owner here is a no-op rather than an
 *   error, which is why the picker does not have to defend against it.
 * - **Required is a choice and advisory is the default.** A required reviewer is
 *   a gate, and a gate should be asked for rather than fallen into — the server
 *   defaults an unspecified reviewer to advisory for exactly that reason, and
 *   this form does not quietly disagree with it.
 *
 * Nothing here filters the picker by who can read the document. The API refuses a
 * candidate who cannot resolve `document.view` with a sentence naming them and
 * the decision that denied them, which is a better answer than a name silently
 * missing from a list — and a client-side filter would be a second copy of §4.6's
 * precedence table.
 */

import { useState } from 'react';
import { useOpenApprovalRequest, usePeoplePicker } from '../../api/queries.ts';
import type { ApprovalMutationResponse, ReviewerRequirement } from '../../api/types.ts';
import { Badge, roleLabel } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select } from '../../components/ui/field.tsx';
import { RequirementBadge } from './vocabulary.tsx';

const REQUIREMENT_OPTIONS: ReadonlyArray<{
  value: ReviewerRequirement;
  label: string;
  description: string;
}> = [
  {
    value: 'advisory',
    label: 'Advisory',
    description: 'Asked to weigh in. Publishing does not wait on them.',
  },
  {
    value: 'required',
    label: 'Required',
    description: 'Publishing waits until they approve.',
  },
];

interface Chosen {
  personId: string;
  name: string;
  requirement: ReviewerRequirement;
}

export interface OpenApprovalDialogProps {
  nodeId: string;
  title: string;
  /** Used only to say who arrives as required by §7.3; the trigger is the guarantee. */
  ownerName?: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpened?: (result: ApprovalMutationResponse) => void;
}

export function OpenApprovalDialog({
  nodeId,
  title,
  ownerName,
  isOpen,
  onOpenChange,
  onOpened,
}: OpenApprovalDialogProps) {
  return (
    <Dialog
      title="Ask for review"
      description={title}
      size="lg"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <OpenApprovalForm
          nodeId={nodeId}
          ownerName={ownerName ?? null}
          onClose={close}
          {...(onOpened ? { onOpened } : {})}
        />
      )}
    </Dialog>
  );
}

function OpenApprovalForm({
  nodeId,
  ownerName,
  onClose,
  onOpened,
}: {
  nodeId: string;
  ownerName: string | null;
  onClose: () => void;
  onOpened?: (result: ApprovalMutationResponse) => void;
}) {
  const picker = usePeoplePicker();
  const open = useOpenApprovalRequest();

  const [chosen, setChosen] = useState<Chosen[]>([]);
  const [candidate, setCandidate] = useState('');
  const [requirement, setRequirement] = useState<ReviewerRequirement>('advisory');

  const people = picker.data?.people ?? [];
  const options = people
    .filter((person) => !chosen.some((row) => row.personId === person.id))
    .map((person) => ({
      value: person.id,
      label: person.name,
      description: `${person.email} · ${roleLabel(person.role)}`,
    }));

  const add = () => {
    const person = people.find((row) => row.id === candidate);
    if (!person) return;
    setChosen((rows) => [...rows, { personId: person.id, name: person.name, requirement }]);
    setCandidate('');
  };

  return (
    <>
      <DialogBody>
        <Alert tone="info" title="What opening a request does">
          <div className="mt-1 flex flex-col gap-2">
            <p>
              A draft moves to <span className="font-mono">in_review</span> (§7.4). A document that
              is already published stays published and keeps its readers on the version they are
              reading — only the draft is under review, and the draft was already invisible to them.
            </p>
            <p>
              {ownerName ?? 'The document owner'} is added as a{' '}
              <span className="font-semibold">required</span> reviewer automatically (§7.3), so the
              required set is never empty.
            </p>
          </div>
        </Alert>

        <div className="flex flex-col gap-3 rounded-lg border border-secondary px-4 py-3">
          <h3 className="text-xs font-semibold tracking-wide text-tertiary uppercase">
            Who else should look at this
          </h3>
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
            <Select
              label="Reviewer"
              options={options}
              selectedKey={candidate === '' ? null : candidate}
              onSelectionChange={setCandidate}
              placeholder={picker.isPending ? 'Loading…' : 'Choose a person'}
            />
            <Select
              label="Requirement"
              options={REQUIREMENT_OPTIONS}
              selectedKey={requirement}
              onSelectionChange={setRequirement}
            />
            <Button variant="secondary" isDisabled={candidate === ''} onPress={add}>
              Add
            </Button>
          </div>

          {chosen.length === 0 ? (
            <p className="text-sm text-tertiary">
              Nobody yet. The owner alone is a legitimate request — it is the smallest set §7.6
              permits, and it still records who approved and when.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {chosen.map((row) => (
                <li key={row.personId} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-primary">{row.name}</span>
                  <RequirementBadge requirement={row.requirement} />
                  <Button
                    size="sm"
                    variant="tertiary"
                    className="ml-auto"
                    onPress={() =>
                      setChosen((rows) => rows.filter((item) => item.personId !== row.personId))
                    }
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* The count that matters, said before the request exists rather than
            after somebody wonders why publishing is still blocked. */}
        <p className="text-sm text-tertiary">
          <Badge tone="warning">Required</Badge> reviewers block publishing;{' '}
          <Badge tone="outline">Advisory</Badge> reviewers never do. Chosen so far:{' '}
          {chosen.filter((row) => row.requirement === 'required').length} required besides the
          owner, {chosen.filter((row) => row.requirement === 'advisory').length} advisory.
        </p>

        {picker.isError ? <ErrorState error={picker.error} /> : null}
        {open.isError ? <ErrorState error={open.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={open.isPending}
          onPress={() =>
            open.mutate(
              {
                documentId: nodeId,
                ...(chosen.length > 0
                  ? {
                      reviewers: chosen.map((row) => ({
                        personId: row.personId,
                        requirement: row.requirement,
                      })),
                    }
                  : {}),
              },
              {
                onSuccess: (result) => {
                  onOpened?.(result);
                  onClose();
                },
              },
            )
          }
        >
          Open the request
        </Button>
      </DialogFooter>
    </>
  );
}
